import { useState } from 'react'
import { Folder } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type { MediaFolderDocument } from '../../services/admin.mediaFolders.service'

interface MediaMoveDialogProps {
  open: boolean
  currentFolderId: string | null
  folders: MediaFolderDocument[]
  onClose: () => void
  onMove: (folderId: string | null) => Promise<void>
  title?: string
  excludeIds?: string[]
}

export function MediaMoveDialog({
  open,
  currentFolderId,
  folders,
  onClose,
  onMove,
  title = 'Move to Folder',
  excludeIds,
}: MediaMoveDialogProps) {
  const excluded = new Set(excludeIds ?? [])
  const visible = folders.filter((f) => !excluded.has(f.id))
  const [selected, setSelected] = useState<string | null>(currentFolderId)
  const [moving, setMoving] = useState(false)

  async function handleMove() {
    setMoving(true)
    try {
      await onMove(selected)
      onClose()
    } finally {
      setMoving(false)
    }
  }

  function renderFolder(folder: MediaFolderDocument, depth = 0): React.ReactNode {
    const children = visible.filter((f) => f.parentId === folder.id)
    return (
      <div key={folder.id}>
        <button
          onClick={() => setSelected(folder.id)}
          className={cn(
            'flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors',
            selected === folder.id ? 'bg-accent font-medium' : 'hover:bg-muted'
          )}
          style={{ paddingLeft: `${(depth + 1) * 16}px` }}
        >
          <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
          {folder.name}
        </button>
        {children.map((c) => renderFolder(c, depth + 1))}
      </div>
    )
  }

  const rootFolders = visible.filter((f) => f.parentId === null)

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="flex max-h-60 flex-col gap-0.5 overflow-y-auto">
          {/* Root option */}
          <button
            onClick={() => setSelected(null)}
            className={cn(
              'flex items-center gap-2 rounded px-2 py-1.5 text-sm font-medium transition-colors',
              selected === null ? 'bg-accent' : 'hover:bg-muted'
            )}
          >
            <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
            Root (All Media)
          </button>
          {rootFolders.map((f) => renderFolder(f))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={moving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleMove()}
            disabled={moving || selected === currentFolderId}
          >
            {moving ? 'Moving…' : 'Move Here'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
